import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { HeartHandshake, ArrowLeft, Send, CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { track, EVENTS } from "@/lib/analytics";
import { setClarityEvent } from "@/lib/clarityEvents";

/**
 * <PartnerInquiry /> — the destination for "Partner with us" from the
 * Partners page. Replaces the previous `mailto:` link which was a
 * dead-click for Facebook-in-app browsers, iOS default-mailer users
 * who had no Mail account configured, and anyone on a shared kiosk.
 *
 * Submissions POST to /api/partner-inquiry, which stores the row and
 * emails ashlee@asvmedicalservices.com via Resend. Also fires the
 * GA4 `partner_referral` conversion event and Clarity `partner_inquiry`
 * custom tag so session recordings can be filtered to real leads.
 */
export default function PartnerInquiry() {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const wantsDemo = params.get("intent") === "demo";
    const [form, setForm] = useState({
        org_name: "", contact_name: "", role: "", email: "", phone: "",
        category: "hospice", state: "", best_time_to_reach: "",
        message: wantsDemo ? "We'd like to schedule a demo of Memory Lane for our team." : "",
    });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.org_name || !form.contact_name || !form.email) {
            toast.error("Please fill in your organization, name, and email.");
            return;
        }
        setBusy(true);
        try {
            // Map our richer form onto the existing /api/partners/interest
            // endpoint (which the founder already gets Resend-notified about).
            // Everything beyond email+org+role gets concatenated into `notes`
            // so no data is lost and no new backend endpoint is needed.
            const notesLines = [
                wantsDemo ? "Demo requested: YES" : null,
                form.phone ? `Phone: ${form.phone}` : null,
                form.category ? `Category: ${form.category}` : null,
                form.state ? `State: ${form.state}` : null,
                form.best_time_to_reach ? `Best time: ${form.best_time_to_reach}` : null,
                form.message ? `\n${form.message}` : null,
            ].filter(Boolean).join("\n");
            await api.post("/partners/interest", {
                email: form.email,
                org_name: form.org_name,
                role: [form.contact_name, form.role].filter(Boolean).join(" · "),
                notes: notesLines,
            });
            setDone(true);
            track(EVENTS.PARTNER_REFERRAL, { source: "partner_inquiry_form", category: form.category });
            setClarityEvent("partner_inquiry_submitted");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't send right now — please email us at ashlee@asvmedicalservices.com.");
        } finally {
            setBusy(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen bg-cream-light flex items-center justify-center p-4" data-testid="partner-inquiry-done">
                <div className="ml-card p-8 max-w-md w-full text-center">
                    <CheckCircle2 size={40} strokeWidth={1.8} className="mx-auto mb-3 text-stage_early-dark" />
                    <h1 className="text-2xl font-heading font-semibold text-[#12776F] mb-2">Thank you.</h1>
                    <p className="text-[#4C5551] mb-6">
                        Ashlee has been notified and will reach out within one business day.
                        In the meantime, feel free to book a 15-minute intro call directly.
                    </p>
                    <div className="flex gap-2 flex-wrap justify-center">
                        <a
                            href={process.env.REACT_APP_BOOKING_URL}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-btn-primary"
                            data-testid="partner-inquiry-book-cal"
                        >
                            Book a 15-min call
                        </a>
                        <button
                            onClick={() => navigate("/partners")}
                            className="px-4 py-2 rounded-2xl border border-cream-dark text-[#4C5551] hover:text-teal-dark text-sm"
                        >
                            Back to Partners
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-cream-light py-8 px-4" data-testid="partner-inquiry-page">
            <div className="max-w-2xl mx-auto">
                <Link to="/partners" className="inline-flex items-center gap-1.5 text-sm text-[#4C5551] hover:text-teal-dark mb-4">
                    <ArrowLeft size={14} strokeWidth={1.8} /> Back to Partners
                </Link>

                <div className="ml-card p-6 md:p-8">
                    <div className="flex items-center gap-2 text-teal-dark mb-1">
                        <HeartHandshake size={20} strokeWidth={1.8} />
                        <span className="text-xs uppercase tracking-wider font-semibold">Partner with Memory Lane</span>
                    </div>
                    <h1 className="text-2xl md:text-3xl font-heading font-semibold text-[#12776F] mb-3">
                        Tell us about your organization.
                    </h1>
                    <p className="text-sm text-[#4C5551] mb-6">
                        We&apos;ll respond within one business day. If you&apos;d rather chat live, book a{" "}
                        <a href={process.env.REACT_APP_BOOKING_URL} target="_blank" rel="noreferrer" className="text-teal-dark underline">15-minute intro call</a>.
                    </p>

                    <form onSubmit={submit} className="space-y-4" data-testid="partner-inquiry-form">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <Field label="Organization" required v={form.org_name} onChange={(v) => setForm({ ...form, org_name: v })} testid="pi-org" />
                            <Field label="Your name" required v={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="pi-name" />
                            <Field label="Your role" placeholder="e.g. Executive Director" v={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="pi-role" />
                            <Field label="Email" type="email" required v={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="pi-email" />
                            <Field label="Phone" v={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="pi-phone" />
                            <label className="block">
                                <span className="text-xs uppercase tracking-wider text-[#4C5551] font-semibold">Category</span>
                                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                                    className="ml-input mt-1" data-testid="pi-category">
                                    <option value="hospice">Hospice</option>
                                    <option value="guide">GUIDE program</option>
                                    <option value="home_health">Home Health</option>
                                    <option value="primary_care">Primary Care / Clinic</option>
                                    <option value="neurology">Neurology / Memory Clinic</option>
                                    <option value="community">Community / Support</option>
                                    <option value="legal_financial">Legal / Financial</option>
                                    <option value="employer">Employer / Benefits</option>
                                    <option value="other">Other</option>
                                </select>
                            </label>
                            <Field label="State (US)" v={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="pi-state" />
                            <Field label="Best time to reach you" v={form.best_time_to_reach} onChange={(v) => setForm({ ...form, best_time_to_reach: v })} testid="pi-time" />
                        </div>
                        <label className="block">
                            <span className="text-xs uppercase tracking-wider text-[#4C5551] font-semibold">
                                Tell us a bit about how you&apos;d like to work together
                            </span>
                            <textarea
                                value={form.message}
                                onChange={(e) => setForm({ ...form, message: e.target.value })}
                                rows={4}
                                className="ml-input mt-1"
                                placeholder="Referrals, integration, GUIDE program, sponsoring a family, other…"
                                data-testid="pi-message"
                            />
                        </label>
                        <div className="flex items-center gap-3 pt-2">
                            <button
                                type="submit"
                                disabled={busy}
                                className="ml-btn-primary inline-flex items-center gap-1.5 disabled:opacity-60"
                                data-testid="pi-submit"
                            >
                                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} strokeWidth={2} />}
                                {busy ? "Sending…" : "Send inquiry"}
                            </button>
                            <span className="text-xs text-[#6E7A74]">
                                Or email us: <a href="mailto:ashlee@asvmedicalservices.com" className="text-teal-dark underline">ashlee@asvmedicalservices.com</a>
                            </span>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

function Field({ label, v, onChange, required, type = "text", placeholder = "", testid }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-wider text-[#4C5551] font-semibold">
                {label} {required && <span className="text-coral-dark">*</span>}
            </span>
            <input
                type={type}
                value={v}
                onChange={(e) => onChange(e.target.value)}
                required={required}
                placeholder={placeholder}
                className="ml-input mt-1"
                data-testid={testid}
            />
        </label>
    );
}
